/**
 * CampaignDetail — tabbed campaign detail page.
 *
 * Replaces the old three-route split (/edit, /submit, /coverage) with one
 * page that fetches the campaign once and holds it in shared state, with
 * a persistent header (status, Publish/Save) and four tabs underneath:
 *
 *   - Overview  — details, playing hours, expected layout
 *   - Assets    — per-zone asset grid + live preview
 *   - Targeting — venue / outlet-group submission (CampaignTargetingPanel)
 *   - Analytics — approval funnel, coverage table, playback health
 *                 (CampaignAnalyticsPanel)
 *
 * Tab selection lives in `?tab=` so links/back-button/bookmarks work.
 * Create mode is unaffected — new campaigns still go through
 * CampaignEditor's single-page flow until they have an id.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReplayIcon from '@mui/icons-material/Replay';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import {
  Advertiser,
  Campaign,
  CampaignAsset,
  CampaignAssetType,
  CreateCampaignAssetRequest,
  Layout,
  LayoutZone,
} from '../types';
import {
  AssetGrid,
  FULLSCREEN_FALLBACK,
  LayoutOption,
  PlayingHoursEditor,
  PreviewStage,
  SectionTitle,
} from '../components/campaign/campaignEditorShared';
import CampaignTargetingPanel from '../components/campaign/CampaignTargetingPanel';
import CampaignAnalyticsPanel from '../components/campaign/CampaignAnalyticsPanel';

type TabKey = 'overview' | 'assets' | 'targeting' | 'analytics';
const TAB_KEYS: TabKey[] = ['overview', 'assets', 'targeting', 'analytics'];

const CampaignDetail: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const basePath = `/${user?.role || 'admin'}`;
  const [searchParams, setSearchParams] = useSearchParams();

  const tab: TabKey = TAB_KEYS.includes(searchParams.get('tab') as TabKey)
    ? (searchParams.get('tab') as TabKey)
    : 'overview';
  const setTab = (next: TabKey) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('tab', next);
      return p;
    });
  };

  // Core data
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [advertisers, setAdvertisers] = useState<Advertiser[]>([]);
  const [layouts, setLayouts] = useState<Layout[]>([]);
  const [uploadingZone, setUploadingZone] = useState<string | null>(null);

  // Form state (Overview tab)
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [advertiserId, setAdvertiserId] = useState('');
  const [categories, setCategories] = useState('OTHER');
  const [targetLayoutId, setTargetLayoutId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [playingHours, setPlayingHours] = useState('');

  // UI flags
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Assets-tab live preview controls.
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [previewResetTick, setPreviewResetTick] = useState(0);

  // ---- bootstrap ----

  useEffect(() => {
    apiService
      .listAdvertisersV2({ limit: 200 })
      .then((r: any) => setAdvertisers(r.data || []))
      .catch(() => setAdvertisers([]));
    apiService
      .listLayouts()
      .then(setLayouts)
      .catch(() => setLayouts([]));
  }, []);

  useEffect(() => {
    if (!campaignId) return;
    let cancelled = false;
    (async () => {
      try {
        const c = await apiService.getCampaign(campaignId);
        if (cancelled) return;
        setCampaign(c);
        setTitle(c.title);
        setDescription(c.description || '');
        setAdvertiserId(c.advertiser_id || '');
        setCategories(c.categories || 'OTHER');
        setTargetLayoutId(c.target_layout_id || '');
        setPlayingHours(c.playing_hours || '');
        setStartDate((c.published_time_start || '').slice(0, 10));
        setEndDate((c.published_time_end || '').slice(0, 10));
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Failed to load campaign');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [campaignId]);

  // ---- derived ----

  const activeLayout = useMemo<Layout | null>(() => {
    if (!targetLayoutId) return null;
    return layouts.find((l) => l.id === targetLayoutId) || null;
  }, [layouts, targetLayoutId]);

  const editableZones = useMemo<LayoutZone[]>(
    () => activeLayout?.zones || [FULLSCREEN_FALLBACK],
    [activeLayout],
  );

  const refreshCampaign = useCallback(async () => {
    if (!campaignId) return;
    const fresh = await apiService.getCampaign(campaignId);
    setCampaign(fresh);
    return fresh;
  }, [campaignId]);

  // ---- handlers ----

  const handleSave = async () => {
    if (!campaign) return;
    if (!title || !startDate || !endDate) {
      setError('Title, start date and end date are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await apiService.updateCampaign(campaign.id, {
        title,
        description,
        categories,
        target_layout_id: targetLayoutId || '',
        playing_hours: playingHours,
        published_time_start: `${startDate}T00:00:00Z`,
        published_time_end: `${endDate}T23:59:59Z`,
      });
      setCampaign(updated);
      setSuccess('Campaign updated.');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!campaign) return;
    setPublishing(true);
    try {
      const updated = await apiService.publishCampaign(campaign.id);
      setCampaign(updated);
      setSuccess('Campaign published. Head to the Targeting tab to submit it to venues.');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  const probeVideoDuration = (file: File): Promise<number> =>
    new Promise((resolve) => {
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.muted = true;
      const url = URL.createObjectURL(file);
      const done = (sec: number) => {
        URL.revokeObjectURL(url);
        resolve(sec);
      };
      v.onloadedmetadata = () => {
        const d = v.duration;
        done(Number.isFinite(d) && d > 0 ? Math.round(d) : 10);
      };
      v.onerror = () => done(10);
      v.src = url;
    });

  const probeVideoDurationFromUrl = (url: string): Promise<number> =>
    new Promise((resolve) => {
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.muted = true;
      let settled = false;
      const done = (sec: number) => {
        if (settled) return;
        settled = true;
        resolve(sec);
      };
      v.onloadedmetadata = () => {
        const d = v.duration;
        done(Number.isFinite(d) && d > 0 ? Math.round(d) : 10);
      };
      v.onerror = () => done(10);
      setTimeout(() => done(10), 5000);
      v.src = url;
    });

  const handleUpload = useCallback(
    async (file: File, zoneSlug: string) => {
      if (!campaign) return;
      setUploadingZone(zoneSlug);
      try {
        const up: any = await apiService.uploadFile(file);
        const contentType: CampaignAssetType = file.type.startsWith('video/') ? 'VIDEO' : 'IMAGE';
        const detected = contentType === 'VIDEO' ? await probeVideoDuration(file) : 8;
        const sortBase = (campaign.assets || []).filter((a) => a.zone_slug === zoneSlug).length;
        const payload: CreateCampaignAssetRequest = {
          zone_slug: zoneSlug,
          content_url: up.url,
          content_type: contentType,
          duration_seconds: detected,
          sort_order: sortBase,
          sha256: up.sha256 || '',
          size_bytes: up.size_bytes || 0,
        };
        await apiService.addCampaignAsset(campaign.id, payload);
        await refreshCampaign();
      } catch (e: any) {
        setError(e?.response?.data?.error || e?.message || 'Upload failed');
      } finally {
        setUploadingZone(null);
      }
    },
    [campaign, refreshCampaign],
  );

  const handleAddByUrl = useCallback(
    async (rawUrl: string, zoneSlug: string) => {
      if (!campaign) return;
      const url = rawUrl.trim();
      if (!url) return;
      try {
        const parsed = new URL(url);
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
          setError('URL must start with http:// or https://');
          return;
        }
      } catch {
        setError("That doesn't look like a valid URL.");
        return;
      }
      const lower = url.toLowerCase().split('?')[0];
      const isVideo = ['.mp4', '.mov', '.m4v', '.webm', '.mkv'].some((ext) => lower.endsWith(ext));
      const isImage = ['.jpg', '.jpeg', '.png', '.gif', '.webp'].some((ext) => lower.endsWith(ext));
      if (!isVideo && !isImage) {
        setError(
          'URL must end with a recognised image or video extension ' +
            '(.mp4 / .mov / .webm / .jpg / .png / .webp / …).',
        );
        return;
      }
      const contentType: CampaignAssetType = isVideo ? 'VIDEO' : 'IMAGE';
      const detected = isVideo ? await probeVideoDurationFromUrl(url) : 8;
      const sortBase = (campaign.assets || []).filter((a) => a.zone_slug === zoneSlug).length;
      const payload: CreateCampaignAssetRequest = {
        zone_slug: zoneSlug,
        content_url: url,
        content_type: contentType,
        duration_seconds: detected,
        sort_order: sortBase,
        sha256: '',
        size_bytes: 0,
      };
      try {
        await apiService.addCampaignAsset(campaign.id, payload);
        await refreshCampaign();
      } catch (e: any) {
        setError(e?.response?.data?.error || e?.message || 'Add by URL failed');
      }
    },
    [campaign, refreshCampaign],
  );

  const handleRemoveAsset = useCallback(
    async (asset: CampaignAsset) => {
      if (!campaign) return;
      if (!window.confirm(`Remove this asset from zone "${asset.zone_slug}"?`)) return;
      try {
        await apiService.deleteCampaignAsset(campaign.id, asset.id);
        await refreshCampaign();
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Delete failed');
      }
    },
    [campaign, refreshCampaign],
  );

  const handleUpdateDuration = useCallback(
    async (asset: CampaignAsset, seconds: number) => {
      if (!campaign) return;
      try {
        await apiService.updateCampaignAsset(campaign.id, asset.id, {
          zone_slug: asset.zone_slug,
          content_url: asset.content_url,
          content_type: asset.content_type,
          duration_seconds: seconds,
          sort_order: asset.sort_order,
          sha256: asset.sha256,
          size_bytes: asset.size_bytes,
        });
        await refreshCampaign();
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Update failed');
      }
    },
    [campaign, refreshCampaign],
  );

  const handleSubmitted = useCallback(async () => {
    await refreshCampaign();
    setTab('analytics');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshCampaign]);

  // ---- render ----

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 320 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!campaign) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Campaign not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Button
        onClick={() => navigate(`${basePath}/campaigns`)}
        sx={{ color: '#1D4ED8', mb: 1, textTransform: 'none' }}
      >
        ← Campaigns
      </Button>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ md: 'flex-end' }}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: '#2563EB',
              mb: 0.5,
            }}
          >
            Campaign
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0F172A' }}>
            {title || campaign.title}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Chip
              label={campaign.state}
              size="small"
              color={
                campaign.state === 'PUBLISHED'
                  ? 'success'
                  : campaign.state === 'REJECTED'
                  ? 'error'
                  : 'default'
              }
            />
            {activeLayout && (
              <Chip label={`Layout · ${activeLayout.display_name}`} size="small" variant="outlined" />
            )}
            <Chip
              label={`${campaign.assets?.length || 0} asset${(campaign.assets?.length || 0) === 1 ? '' : 's'}`}
              size="small"
              variant="outlined"
            />
          </Stack>
        </Box>
        <Stack direction="row" spacing={1} sx={{ mt: { xs: 2, md: 0 } }}>
          {campaign.state === 'DRAFT' && (campaign.assets?.length || 0) > 0 && (
            <Button variant="outlined" startIcon={<LaunchIcon />} onClick={handlePublish} disabled={publishing}>
              {publishing ? 'Publishing…' : 'Publish'}
            </Button>
          )}
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, borderBottom: '1px solid #E2E8F0' }}
      >
        <Tab value="overview" label="Overview" />
        <Tab value="assets" label="Assets" />
        <Tab value="targeting" label="Targeting" />
        <Tab value="analytics" label="Analytics" />
      </Tabs>

      {tab === 'overview' && (
        <Box sx={{ maxWidth: 760 }}>
          <Paper variant="outlined" sx={{ p: 3, mb: 2, borderRadius: 3 }}>
            <SectionTitle>Details</SectionTitle>
            <Stack spacing={2}>
              <TextField label="Title" fullWidth value={title} onChange={(e) => setTitle(e.target.value)} />
              <TextField
                label="Advertiser"
                fullWidth
                value={advertisers.find((a) => a.id === advertiserId)?.display_name || advertiserId}
                disabled
                helperText="Advertiser cannot be changed after creation"
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                minRows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <TextField
                label="Categories (CSV)"
                fullWidth
                value={categories}
                onChange={(e) => setCategories(e.target.value)}
                helperText="e.g. FNB, BEAUTY"
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Start"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <TextField
                  label="End"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Stack>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 3, mb: 2, borderRadius: 3 }}>
            <SectionTitle>Playing hours</SectionTitle>
            <Typography sx={{ fontSize: 13, color: '#64748B', mb: 2 }}>
              When during the day this campaign is eligible to play. The
              outer date range above is the campaign's whole booking
              window; this is the per-day slot inside it. Devices apply
              their own local clock when evaluating.
            </Typography>
            <PlayingHoursEditor value={playingHours} onChange={setPlayingHours} />
          </Paper>

          <Paper variant="outlined" sx={{ p: 3, mb: 2, borderRadius: 3 }}>
            <SectionTitle>Expected layout</SectionTitle>
            <Typography sx={{ fontSize: 13, color: '#64748B', mb: 2 }}>
              Pick the layout your campaign is designed for. The Assets tab
              will surface one slot per zone and the preview shows how
              it'll play. You can change this at any time before
              publishing.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 1.5 }}>
              <LayoutOption
                label="Fullscreen (default)"
                slug="—"
                zones={[FULLSCREEN_FALLBACK]}
                selected={!targetLayoutId}
                onSelect={() => setTargetLayoutId('')}
              />
              {layouts.map((l) => (
                <LayoutOption
                  key={l.id}
                  label={l.display_name}
                  slug={l.slug}
                  zones={l.zones}
                  selected={targetLayoutId === l.id}
                  onSelect={() => setTargetLayoutId(l.id)}
                />
              ))}
            </Box>
          </Paper>
        </Box>
      )}

      {tab === 'assets' && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 420px' },
            gap: 4,
            alignItems: 'flex-start',
          }}
        >
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
            <SectionTitle>Assets by zone — {activeLayout?.display_name || 'fullscreen'}</SectionTitle>
            <Typography sx={{ fontSize: 13, color: '#64748B', mb: 2 }}>
              One creative per zone. Files upload to S3 right away;
              SHA-256 + size are stamped for cache validation on the
              player.
            </Typography>
            <AssetGrid
              zones={editableZones}
              assets={campaign.assets || []}
              uploadingZone={uploadingZone}
              onUpload={handleUpload}
              onAddByUrl={handleAddByUrl}
              onRemove={handleRemoveAsset}
              onDurationChange={handleUpdateDuration}
            />
          </Paper>

          <Box sx={{ position: { md: 'sticky' }, top: 24 }}>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, boxShadow: '0 1px 2px rgba(120,90,60,0.06)' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: '#2563EB',
                  }}
                >
                  Live preview
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title={previewPlaying ? 'Pause' : 'Play'}>
                    <IconButton
                      size="small"
                      onClick={() => setPreviewPlaying((p) => !p)}
                      disabled={(campaign.assets?.length || 0) === 0}
                    >
                      {previewPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Restart from beginning">
                    <IconButton
                      size="small"
                      onClick={() => setPreviewResetTick((t) => t + 1)}
                      disabled={(campaign.assets?.length || 0) === 0}
                    >
                      <ReplayIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
              <Typography sx={{ fontSize: 13, color: '#64748B', mb: 2 }}>
                {activeLayout
                  ? `${activeLayout.display_name} · ${activeLayout.zones.length} zone${activeLayout.zones.length === 1 ? '' : 's'}`
                  : 'Fullscreen (single main zone)'}
              </Typography>
              <PreviewStage
                zones={editableZones}
                assets={campaign.assets || []}
                playing={previewPlaying}
                resetTick={previewResetTick}
              />
              <Typography sx={{ fontSize: 11, color: '#94A3B8', mt: 1.5, textAlign: 'center' }}>
                16:9 aspect · videos muted in preview · zones rotate per
                asset duration
              </Typography>
            </Paper>
          </Box>
        </Box>
      )}

      {tab === 'targeting' && (
        <CampaignTargetingPanel campaign={campaign} onSubmitted={handleSubmitted} />
      )}

      {tab === 'analytics' && <CampaignAnalyticsPanel campaign={campaign} />}
    </Box>
  );
};

export default CampaignDetail;
